import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["textarea"]
  static values = {
    url: String // e.g. /boards/:bo_table/posts/:wr_id/files
  }

  connect() {
    this.boundPaste = this.handlePaste.bind(this)
    this.boundDrop = this.handleDrop.bind(this)
    this.boundDragOver = this.handleDragOver.bind(this)
    this.boundDragEnter = this.handleDragEnter.bind(this)
    this.boundDragLeave = this.handleDragLeave.bind(this)

    this.textareaTarget.addEventListener("paste", this.boundPaste)
    this.textareaTarget.addEventListener("drop", this.boundDrop)
    this.textareaTarget.addEventListener("dragover", this.boundDragOver)
    this.textareaTarget.addEventListener("dragenter", this.boundDragEnter)
    this.textareaTarget.addEventListener("dragleave", this.boundDragLeave)
  }

  disconnect() {
    this.textareaTarget.removeEventListener("paste", this.boundPaste)
    this.textareaTarget.removeEventListener("drop", this.boundDrop)
    this.textareaTarget.removeEventListener("dragover", this.boundDragOver)
    this.textareaTarget.removeEventListener("dragenter", this.boundDragEnter)
    this.textareaTarget.removeEventListener("dragleave", this.boundDragLeave)
    this.removeToast()
  }

  handlePaste(event) {
    const items = Array.from(event.clipboardData?.items || [])
    const imageItems = items.filter(item => item.type.startsWith("image/"))
    if (imageItems.length === 0) return

    event.preventDefault()
    event.stopPropagation()
    imageItems.forEach(item => {
      const file = item.getAsFile()
      if (file) this.uploadImage(file)
    })
  }

  handleDragOver(event) {
    // Always prevent default to allow drop on textarea
    event.preventDefault()
  }

  handleDragEnter(event) {
    event.preventDefault()
    const files = event.dataTransfer?.items || []
    const hasImage = Array.from(files).some(item => item.type.startsWith("image/"))
    if (hasImage) {
      this.textareaTarget.classList.add("ring-2", "ring-blue-400", "border-blue-400")
    }
  }

  handleDragLeave(event) {
    // Only remove highlight when actually leaving the textarea
    if (!this.textareaTarget.contains(event.relatedTarget)) {
      this.textareaTarget.classList.remove("ring-2", "ring-blue-400", "border-blue-400")
    }
  }

  handleDrop(event) {
    event.preventDefault()
    this.textareaTarget.classList.remove("ring-2", "ring-blue-400", "border-blue-400")

    const files = Array.from(event.dataTransfer?.files || [])
    if (files.length === 0) return

    const images = files.filter(f => f.type.startsWith("image/"))
    const others = files.filter(f => !f.type.startsWith("image/"))

    images.forEach(file => this.uploadImage(file))

    if (others.length > 0) {
      this.showToast(`이미지가 아닌 파일(${others.map(f => f.name).join(", ")})은 아래 파일 첨부 영역을 이용해주세요.`)
    }
  }

  async uploadImage(file) {
    const textarea = this.textareaTarget
    // Unique placeholder prevents collision when uploading multiple files
    const uid = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
    const placeholder = `![uploading-${uid}]()`
    const start = textarea.selectionStart
    const text = textarea.value

    textarea.value = text.substring(0, start) + placeholder + text.substring(start)
    textarea.dispatchEvent(new Event("input", { bubbles: true }))

    const formData = new FormData()
    formData.append("file", file)
    const form = this.element.closest("form")
    formData.append("upload_token", form.querySelector('[name="upload_token"]').value)
    form.dataset.pendingUploads = Number(form.dataset.pendingUploads || 0) + 1

    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        },
        body: formData
      })

      if (!response.ok) throw new Error("Upload failed")

      const data = await response.json()
      const markdown = `![${data.filename}](${data.url})`

      textarea.value = textarea.value.replace(placeholder, markdown)
      textarea.dispatchEvent(new Event("input", { bubbles: true }))
    } catch (error) {
      textarea.value = textarea.value.replace(placeholder, "")
      textarea.dispatchEvent(new Event("input", { bubbles: true }))
      this.showToast("이미지 업로드에 실패했습니다. 다시 시도해주세요.")
      console.error("Image upload failed:", error)
    } finally {
      form.dataset.pendingUploads = Math.max(0, Number(form.dataset.pendingUploads || 0) - 1)
    }
  }

  showToast(message) {
    this.removeToast()
    const toast = document.createElement("div")
    toast.id = "image-upload-toast"
    toast.className = "fixed bottom-4 left-1/2 -translate-x-1/2 z-50 px-4 py-2 bg-gray-800 text-white text-sm rounded-lg shadow-lg max-w-sm text-center"
    toast.textContent = message
    document.body.appendChild(toast)
    setTimeout(() => this.removeToast(), 4000)
  }

  removeToast() {
    document.getElementById("image-upload-toast")?.remove()
  }
}
